import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, resolveDefaultPieces } from "../lib/apiClient";
import { PageHeader, ActionButton } from "../components/Layout";
import { PlusCircle, Trash2, Save, Layers, PackageOpen, Package, Sofa, Armchair, Info } from "lucide-react";
import { toast } from "sonner";

const TEMPLATES = {
  SET:          { label: "Set", icon: Layers, categories: ["Baza", "Başlık", "Yatak"], sizeMode: "select", requireSize: true },
  BAZA_BASLIK:  { label: "Baza + Başlık", icon: PackageOpen, categories: ["Baza", "Başlık"], sizeMode: "select", requireSize: true },
  BAZA:         { label: "Baza", icon: PackageOpen, categories: ["Baza"], sizeMode: "select", requireSize: true },
  BASLIK:       { label: "Başlık", icon: Package, categories: ["Başlık"], sizeMode: "select", requireSize: true },
  YATAK:        { label: "Yatak", icon: Layers, categories: ["Yatak"], sizeMode: "select", requireSize: true },
  KOMODIN:      { label: "Komodin", icon: Armchair, categories: ["Komodin"], sizeMode: "none", requireSize: false },
  BENCH:        { label: "Bench", icon: Sofa, categories: ["Bench"], sizeMode: "text", requireSize: false },
};

function todayISO() { return new Date().toISOString().slice(0, 10); }
const emptyGroup = (type) => ({ type, model: "", size: "", fabric: "", quantity: 1, notes: "" });

export default function OrderNewPage() {
  const navigate = useNavigate();
  const [customer_name, setCustomer] = useState("");
  const [reference, setReference] = useState("");
  const [order_date, setOrderDate] = useState(todayISO());
  const [requested_shipment_date, setShipDate] = useState(todayISO());
  const [destination, setDestination] = useState("");
  const [notes, setNotes] = useState("");
  const [groups, setGroups] = useState([]);
  const [fabrics, setFabrics] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [singleCategory, setSingleCategory] = useState("BAZA");
  const [loading, setLoading] = useState(false);
  const [pieceRules, setPieceRules] = useState([]);

  useEffect(() => {
    api.get("/fabrics").then(({ data }) => setFabrics(data)).catch(() => {});
    api.get("/sizes").then(({ data }) => setSizes(data)).catch(() => {});
    api.get("/piece-rules").then(({ data }) => setPieceRules(data)).catch(() => {});
  }, []);

  // DB kurallarından (Kategori, Model VEYA Ölçü) → parça sayısı.
  // Baza için ölçü bazlı; diğer kategoriler için model bazlı.
  const resolvePieces = (cat, model, size) => {
    const c = (cat || "").trim();
    const s = (size || "").trim().toLowerCase();
    const m = (model || "").trim().toLowerCase();
    if (c === "Baza") {
      // Önce ölçü tam eşleşmesi
      const exact = pieceRules.find(r => r.category === "Baza" && (r.size || "").toLowerCase() === s && s);
      if (exact) return exact.pieces_per_product;
      // Wildcard: size boş, model="*"
      const wild = pieceRules.find(r => r.category === "Baza" && !r.size && r.model === "*");
      if (wild) return wild.pieces_per_product;
      return resolveDefaultPieces(c, model);
    }
    const exact = pieceRules.find(r => r.category === c && (r.model || "").toLowerCase() === m);
    if (exact) return exact.pieces_per_product;
    const wild = pieceRules.find(r => r.category === c && r.model === "*");
    if (wild) return wild.pieces_per_product;
    return resolveDefaultPieces(c, model);
  };

  const addGroup = (type) => {
    // copy last group's fabric as default for convenience
    const last = groups[groups.length - 1];
    setGroups([...groups, { ...emptyGroup(type), fabric: last?.fabric || "" }]);
  };
  const updateGroup = (i, patch) => setGroups(groups.map((g, idx) => idx === i ? { ...g, ...patch } : g));
  const removeGroup = (i) => setGroups(groups.filter((_, idx) => idx !== i));

  const totalLines = groups.reduce((s, g) => s + TEMPLATES[g.type].categories.length, 0);
  const totalQty = groups.reduce((s, g) => s + (Number(g.quantity) || 0) * TEMPLATES[g.type].categories.length, 0);

  const submit = async (e) => {
    e.preventDefault();
    if (groups.length === 0) return toast.error("En az bir ürün ekleyin");
    for (const g of groups) {
      const tpl = TEMPLATES[g.type];
      if (!g.model) return toast.error(`${tpl.label} için model adı zorunludur`);
      if (tpl.requireSize && !g.size) return toast.error(`${tpl.label} için ölçü zorunludur`);
    }
    const products = [];
    groups.forEach(g => {
      TEMPLATES[g.type].categories.forEach(cat => {
        products.push({
          category: cat,
          model: g.model,
          size: g.size || "",
          quantity: g.quantity,
          fabric: g.fabric || "",
          notes: g.notes || "",
          pieces_per_product: resolvePieces(cat, g.model, g.size),
        });
      });
    });
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        customer_name, reference, order_date, requested_shipment_date,
        destination, notes, products,
      });
      toast.success(`Sipariş oluşturuldu — ${data.order_number}`);
      navigate(`/siparisler/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setLoading(false);
  };

  return (
    <div>
      <PageHeader title="Yeni Sipariş" subtitle="Sipariş numarası otomatik verilir. Her ürünün kumaşı ayrı seçilir." />

      <form onSubmit={submit} className="space-y-6" data-testid="new-order-form">
        <section className="card-industrial p-6">
          <h2 className="font-display font-bold uppercase tracking-wide text-sm text-slate-500 mb-4">Sipariş Bilgileri</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Field label="Müşteri Adı" required>
              <input data-testid="order-customer" required value={customer_name} onChange={(e) => setCustomer(e.target.value)} className={inputCls} />
            </Field>
            <Field label="Referans (Müşteri PO)">
              <input data-testid="order-reference" value={reference} onChange={(e) => setReference(e.target.value)} className={inputCls + " font-mono"} placeholder="PO-2026-..." />
            </Field>
            <Field label="Sipariş Tarihi" required>
              <input data-testid="order-date" required type="date" value={order_date} onChange={(e) => setOrderDate(e.target.value)} className={inputCls + " font-mono"} />
            </Field>
            <Field label="Sevkiyat Tarihi" required>
              <input data-testid="order-shipdate" required type="date" value={requested_shipment_date} onChange={(e) => setShipDate(e.target.value)} className={inputCls + " font-mono"} />
            </Field>
            <Field label="Varış / Ülke" className="md:col-span-2">
              <input data-testid="order-destination" value={destination} onChange={(e) => setDestination(e.target.value)} className={inputCls} placeholder="İstanbul / Türkiye" />
            </Field>
            <Field label="Notlar" className="md:col-span-2">
              <input data-testid="order-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} />
            </Field>
          </div>
        </section>

        <section className="card-industrial p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold uppercase tracking-wide text-sm text-slate-500">Ürünler</h2>
            <div className="text-xs text-slate-500 flex items-center gap-1"><Info size={14}/> Her ürün için kumaş ayrı seçilir. Komodin ölçüsüzdür; Bench için ölçüyü manuel yazın.</div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-6">
            <TemplateBtn onClick={() => addGroup("SET")} label="Set" hint="Baza + Başlık + Yatak" primary testid="tpl-set" />
            <TemplateBtn onClick={() => addGroup("BAZA_BASLIK")} label="Baza + Başlık" hint="Yataksız" testid="tpl-baza-baslik" />
            <div className="flex items-stretch">
              <select value={singleCategory} onChange={(e) => setSingleCategory(e.target.value)} className="px-3 py-2 border-2 border-r-0 border-slate-800 bg-white text-sm font-bold" data-testid="tpl-single-cat">
                <option value="BAZA">Baza</option>
                <option value="BASLIK">Başlık</option>
                <option value="YATAK">Yatak</option>
              </select>
              <button type="button" onClick={() => addGroup(singleCategory)} data-testid="tpl-single-add" className="flex-1 px-3 py-2 border-2 border-slate-800 bg-white hover:bg-slate-900 hover:text-white font-bold text-sm inline-flex items-center gap-1 justify-center">
                <PlusCircle size={14}/> Tek Ekle
              </button>
            </div>
            <TemplateBtn onClick={() => addGroup("KOMODIN")} label="+ Komodin" hint="Ölçüsüz" testid="tpl-komodin" />
            <TemplateBtn onClick={() => addGroup("BENCH")} label="+ Bench" hint="Ölçü manuel" testid="tpl-bench" />
          </div>

          {groups.length === 0 && (
            <div className="text-center py-10 text-slate-500 border-2 border-dashed border-slate-300">
              Yukarıdan bir şablon ile ürün ekleyin.
            </div>
          )}

          <div className="space-y-3">
            {groups.map((g, i) => {
              const tpl = TEMPLATES[g.type];
              const Icon = tpl.icon;
              return (
                <div key={i} data-testid={`group-line-${i}`} className="border border-slate-300 p-4 bg-slate-50">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-900 text-white text-xs uppercase tracking-widest font-bold">
                      <Icon size={14}/> {tpl.label}
                    </div>
                    <div className="text-xs text-slate-500">
                      → {tpl.categories.join(" + ")} olarak {tpl.categories.length} ürün satırı oluşturulacak
                    </div>
                    <button type="button" onClick={() => removeGroup(i)} data-testid={`remove-group-${i}`} className="ml-auto text-red-700 hover:text-red-900 inline-flex items-center gap-1 text-sm font-bold">
                      <Trash2 size={14} /> Kaldır
                    </button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <Field label="Model" required>
                      <input required value={g.model} onChange={(e) => updateGroup(i, { model: e.target.value })} className={inputCls} placeholder="Capella" data-testid={`group-model-${i}`} />
                      {g.model && (
                        <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`group-piece-hint-${i}`}>
                          {tpl.categories.map(cat => {
                            const n = resolvePieces(cat, g.model, g.size);
                            return (
                              <span key={cat} className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide border ${n > 1 ? "bg-blue-50 border-blue-400 text-blue-800" : "bg-slate-50 border-slate-300 text-slate-500"}`}>
                                {cat}: {n} parça
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </Field>
                    <Field label="Ölçü" required={tpl.requireSize}>
                      {tpl.sizeMode === "none" ? (
                        <div className={inputCls + " text-slate-400 italic"} data-testid={`group-size-none-${i}`}>Ölçüsüz</div>
                      ) : tpl.sizeMode === "text" ? (
                        <input
                          value={g.size}
                          onChange={(e) => updateGroup(i, { size: e.target.value })}
                          list={`bench-sizes-${i}`}
                          placeholder="Örn. 100x40 veya özel..."
                          className={inputCls + " font-mono"}
                          data-testid={`group-size-text-${i}`}
                        />
                      ) : (
                        <SizeSelect sizes={sizes} value={g.size} onChange={(v) => updateGroup(i, { size: v })} testid={`group-size-${i}`} required />
                      )}
                      {tpl.sizeMode === "text" && (
                        <datalist id={`bench-sizes-${i}`}>
                          {sizes.map(s => <option key={s.id} value={s.name} />)}
                          <option value="100x40" />
                          <option value="120x40" />
                          <option value="140x40" />
                          <option value="160x40" />
                          <option value="180x40" />
                        </datalist>
                      )}
                    </Field>
                    <Field label="Kumaş">
                      <FabricSelect fabrics={fabrics} value={g.fabric} onChange={(v) => updateGroup(i, { fabric: v })} testid={`group-fabric-select-${i}`} />
                    </Field>
                    <Field label="Adet" required>
                      <input required type="number" min="1" value={g.quantity} onChange={(e) => updateGroup(i, { quantity: parseInt(e.target.value || "1") })} className={inputCls + " font-mono tabular-nums"} data-testid={`group-qty-${i}`} />
                    </Field>
                    <Field label="Notlar">
                      <input value={g.notes} onChange={(e) => updateGroup(i, { notes: e.target.value })} className={inputCls} data-testid={`group-notes-${i}`} placeholder="Bu ürüne özel not (üretim listesinde görünür)" />
                    </Field>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <div className="flex items-center justify-between p-4 bg-slate-900 text-white sticky bottom-4 z-10 shadow-lg">
          <div className="flex gap-6">
            <div>
              <div className="text-xs uppercase tracking-widest opacity-70">Ürün Satırı</div>
              <div className="font-display font-black text-3xl tabular-nums" data-testid="total-lines-preview">{totalLines}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest opacity-70">Toplam Adet</div>
              <div className="font-display font-black text-3xl tabular-nums" data-testid="total-qty-preview">{totalQty}</div>
            </div>
          </div>
          <ActionButton type="submit" disabled={loading || groups.length === 0} data-testid="submit-order-btn">
            <Save size={18} /> {loading ? "Kaydediliyor..." : "Siparişi Oluştur"}
          </ActionButton>
        </div>
      </form>
    </div>
  );
}

const inputCls = "w-full px-3 py-2 border border-slate-400 bg-white rounded-none focus:border-blue-700 focus:outline-none";

function Field({ label, children, className, required }) {
  return (
    <label className={"block " + (className || "")}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1">
        {label}{required && <span className="text-red-600"> *</span>}
      </div>
      {children}
    </label>
  );
}

function TemplateBtn({ onClick, label, hint, primary, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} className={`flex flex-col items-center justify-center py-3 border-2 font-bold text-sm ${primary ? "bg-blue-700 text-white border-blue-800 hover:bg-blue-800" : "bg-white text-slate-900 border-slate-800 hover:bg-slate-900 hover:text-white"}`}>
      <span className="inline-flex items-center gap-1"><PlusCircle size={14}/> {label}</span>
      <span className={`text-[10px] font-normal ${primary ? "text-blue-100" : "text-slate-500"}`}>{hint}</span>
    </button>
  );
}

export function FabricPicker({ fabrics, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {fabrics.length === 0 && <div className="text-xs text-slate-500 italic">Henüz kumaş tanımlanmamış — Kumaşlar sayfasından ekleyin.</div>}
      {fabrics.map((f) => {
        const active = value === f.name;
        return (
          <button
            key={f.id}
            type="button"
            onClick={() => onChange(active ? "" : f.name)}
            data-testid={`fabric-${f.name}`}
            className={`inline-flex items-center px-3 py-1.5 border-2 text-sm font-semibold ${active ? "border-blue-700 bg-blue-50 text-blue-900" : "border-slate-300 bg-white text-slate-700 hover:border-slate-800"}`}
          >
            {f.name}
          </button>
        );
      })}
      {value && !fabrics.find(f => f.name === value) && (
        <span className="inline-flex items-center px-3 py-1.5 border-2 border-blue-700 bg-blue-50 text-blue-900 text-sm font-semibold">
          {value}
        </span>
      )}
    </div>
  );
}

export function SizeSelect({ sizes, value, onChange, testid, required }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls + " font-mono"} data-testid={testid} required={required}>
      <option value="">Ölçü seçin...</option>
      {sizes.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
      {value && !sizes.find(s => s.name === value) && <option value={value}>{value}</option>}
    </select>
  );
}

// Ölçü seçimiyle aynı stil — kumaş dropdown
export function FabricSelect({ fabrics, value, onChange, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid}>
      <option value="">Kumaş seçin...</option>
      {fabrics.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
      {value && !fabrics.find(f => f.name === value) && <option value={value}>{value}</option>}
    </select>
  );
}
